import React, { useState } from 'react';
import { TrainingData } from './TrainingWizard';
import { CalendarIcon, ClockIcon, MapPinIcon, UserIcon, UsersIcon, FileTextIcon, BuildingIcon, CheckIcon } from 'lucide-react';
import { Badge } from '../ui/Badge';
interface SummaryStepProps {
  trainingData: TrainingData;
  updateTrainingData: (data: Partial<TrainingData>) => void;
}
export function SummaryStep({
  trainingData,
  updateTrainingData
}: SummaryStepProps) {
  const [notes, setNotes] = useState(trainingData.notes || '');
  // Update notes
  const handleNotesChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    setNotes(e.target.value);
    updateTrainingData({
      notes: e.target.value
    });
  };
  return <div data-id="element-3362">
      <h2 className="text-lg font-medium text-gray-900 mb-4" data-id="element-3363">
        Samenvatting Training
      </h2>
      <div className="space-y-6" data-id="element-3364">
        {/* Training details */}
        <div className="bg-white p-4 rounded-lg border border-gray-200" data-id="element-3365">
          <h3 className="font-medium text-gray-900 mb-3" data-id="element-3366">Trainingsgegevens</h3>
          <div className="space-y-3" data-id="element-3367">
            <div className="flex items-start" data-id="element-3368">
              <div className="w-28 text-gray-500" data-id="element-3369">Type:</div>
              <div className="font-medium" data-id="element-3370">{trainingData.trainingTypeName}</div>
            </div>
            <div className="flex items-start" data-id="element-3371">
              <div className="w-28 text-gray-500" data-id="element-3372">Datum:</div>
              <div className="flex items-center" data-id="element-3373">
                <CalendarIcon className="h-4 w-4 mr-1 text-gray-500" data-id="element-3374" />
                <span data-id="element-3375">
                  {trainingData.startDate?.toLocaleDateString('nl-NL', {
                  weekday: 'long',
                  day: 'numeric',
                  month: 'long',
                  year: 'numeric'
                })}
                </span>
              </div>
            </div>
            <div className="flex items-start" data-id="element-3376">
              <div className="w-28 text-gray-500" data-id="element-3377">Tijd:</div>
              <div className="flex items-center" data-id="element-3378">
                <ClockIcon className="h-4 w-4 mr-1 text-gray-500" data-id="element-3379" />
                <span data-id="element-3380">
                  {trainingData.startDate?.toLocaleTimeString('nl-NL', {
                  hour: '2-digit',
                  minute: '2-digit'
                })}{' '}
                  -{' '}
                  {trainingData.endDate?.toLocaleTimeString('nl-NL', {
                  hour: '2-digit',
                  minute: '2-digit'
                })}
                </span>
              </div>
            </div>
            <div className="flex items-start" data-id="element-3381">
              <div className="w-28 text-gray-500" data-id="element-3382">Locatie:</div>
              <div className="flex items-center" data-id="element-3383">
                <MapPinIcon className="h-4 w-4 mr-1 text-gray-500" data-id="element-3384" />
                <span data-id="element-3385">{trainingData.locationName}</span>
              </div>
            </div>
            <div className="flex items-start" data-id="element-3386">
              <div className="w-28 text-gray-500" data-id="element-3387">Instructeur:</div>
              <div className="flex items-center" data-id="element-3388">
                <UserIcon className="h-4 w-4 mr-1 text-gray-500" data-id="element-3389" />
                <span data-id="element-3390">{trainingData.instructorName}</span>
              </div>
            </div>
            {trainingData.clientName && <div className="flex items-start" data-id="element-3391">
                <div className="w-28 text-gray-500" data-id="element-3392">Klant:</div>
                <div className="flex items-center" data-id="element-3393">
                  <BuildingIcon className="h-4 w-4 mr-1 text-gray-500" data-id="element-3394" />
                  <span data-id="element-3395">{trainingData.clientName}</span>
                </div>
              </div>}
            <div className="flex items-start" data-id="element-3396">
              <div className="w-28 text-gray-500" data-id="element-3397">Status:</div>
              <Badge variant="warning" data-id="element-3398">In Afwachting</Badge>
            </div>
          </div>
        </div>
        {/* Participants */}
        <div className="bg-white p-4 rounded-lg border border-gray-200" data-id="element-3399">
          <div className="flex justify-between items-center mb-3" data-id="element-3400">
            <h3 className="font-medium text-gray-900" data-id="element-3401">Deelnemers</h3>
            <Badge variant="default" data-id="element-3402">
              <UsersIcon className="h-3 w-3 mr-1" data-id="element-3403" />
              {trainingData.participants.length} Deelnemers
            </Badge>
          </div>
          {trainingData.participants.length > 0 ? <div className="overflow-x-auto" data-id="element-3404">
              <table className="min-w-full divide-y divide-gray-200" data-id="element-3405">
                <thead className="bg-gray-50" data-id="element-3406">
                  <tr data-id="element-3407">
                    <th scope="col" className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-3408">
                      Naam
                    </th>
                    <th scope="col" className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-3409">
                      Email
                    </th>
                    <th scope="col" className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-3410">
                      Bedrijf
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200" data-id="element-3411">
                  {trainingData.participants.map(participant => <tr key={participant.id} data-id="element-3412">
                      <td className="px-4 py-2 whitespace-nowrap text-sm text-gray-900" data-id="element-3413">
                        {participant.name}
                      </td>
                      <td className="px-4 py-2 whitespace-nowrap text-sm text-gray-500" data-id="element-3414">
                        {participant.email}
                      </td>
                      <td className="px-4 py-2 whitespace-nowrap text-sm text-gray-500" data-id="element-3415">
                        {participant.company || '-'}
                      </td>
                    </tr>)}
                </tbody>
              </table>
            </div> : <div className="text-center py-4 text-gray-500" data-id="element-3416">
              Geen deelnemers toegevoegd
            </div>}
        </div>
        {/* Notes */}
        <div className="bg-white p-4 rounded-lg border border-gray-200" data-id="element-3417">
          <div className="flex items-center mb-3" data-id="element-3418">
            <FileTextIcon className="h-4 w-4 mr-1 text-gray-500" data-id="element-3419" />
            <h3 className="font-medium text-gray-900" data-id="element-3420">Notities</h3>
          </div>
          <textarea rows={4} className="w-full p-2 border border-gray-300 rounded-md" placeholder="Voeg eventuele notities of instructies toe voor deze training..." value={notes} onChange={handleNotesChange} data-id="element-3421"></textarea>
        </div>
        {/* Confirmation */}
        <div className="bg-green-50 p-4 rounded-lg border border-green-200" data-id="element-3422">
          <div className="flex items-start" data-id="element-3423">
            <div className="flex-shrink-0" data-id="element-3424">
              <CheckIcon className="h-5 w-5 text-green-500" data-id="element-3425" />
            </div>
            <div className="ml-3" data-id="element-3426">
              <h3 className="text-sm font-medium text-green-800" data-id="element-3427">
                Klaar om de training in te plannen
              </h3>
              <div className="mt-2 text-sm text-green-700" data-id="element-3428">
                <p data-id="element-3429">
                  Controleer de bovenstaande gegevens. Klik op "Training
                  Inplannen" om de training toe te voegen aan de agenda.
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>;
}